<template>
    <div
        class="flex flex-col"
        :class="fitContent ? 'w-fit max-w-full grow-0' : width === 'single' ? '' : 'grow'"
    >
        <div v-show="showLabel" class="flex flex-row items-center mb-2">
            <label
                v-show="label"
                :for="slugify(label)"
                class="block font-medium font-fancy text-text py-1"
                :class="{'text-xl': bigLabel, 'text-sm': !bigLabel}"
            >{{ label }}</label>
            <span
                v-show="required && !readonly"
                class="text-sm ml-2 text-primary border border-primary px-2 py-1 rounded-md"
            >Required</span>
            <span
                v-show="translate"
                class="text-sm ml-2 text-primary border border-primary px-2 py-1 rounded-md"
            >Translatable</span>
        </div>

        <div class="relative" :class="{ 'w-fit max-w-full': fitContent }">
            <pre
                v-if="type === 'textArea' && renderer === 'code'"
                :id="slugify(label)"
                class="overflow-x-scroll"
                :class="[getClass]"
                :aria-readonly="readonly"
                @input="onInput"
                :contenteditable="!readonly"
            >{{ modelProxy }}</pre>


            <!-- <textarea
              v-if="type === 'textArea' && code === true"
              :id="slugify(label)"
              class="overflow-x-scroll"
              :class="[getClass]"
              :placeholder="placeholder"
              required
              :aria-readonly="readonly"
              :readonly="readonly"
              @input="onInput"
            /> -->
            <textarea
                v-else-if="renderer === 'textarea'"
                :id="slugify(label)"
                class="transition-colors duration-200 min-h-[2.6rem]"
                :class="[getClass]"
                :placeholder="placeholder ?? t('placeholder')"
                required
                :aria-readonly="readonly"
                :readonly="readonly"
                @input="onInput"
                :value="modelProxy"
            />
            <!-- <textarea
              v-if="type === 'textArea'"
              :id="slugify(label)"
              class="ltr bg-background-accent border border-background-accent-dark text-text placeholder:text-text-light text-sm rounded-lg focus:ring-primary focus:border-primary block w-full p-2.5 overflow-y-scroll"
              :class="readonly === true ? 'focus:border-error' : ''"
              :placeholder="placeholder"
              required
              :aria-readonly="readonly"
              :readonly="readonly"
              @input="onInput"
              v-model="value"
              wrap="soft"
            /> -->
            <input
                v-else-if="type === 'float'"
                type="number"
                :id="slugify(label)"
                :class="getClass"
                step="0.1"
                class="transition-colors duration-200"
                :placeholder="placeholder ?? t('placeholder')"
                required
                :value="modelProxy"
                :readonly="readonly"
                @input="onInput"
            />
            <input
                v-else-if="type === 'int'"
                type="number"
                :id="slugify(label)"
                :class="getClass"
                class="transition-colors duration-200"
                :placeholder="placeholder ?? t('placeholder')"
                required
                :value="modelProxy"
                :readonly="readonly"
                @input="onInput"
            />
            <input
                v-else-if="type === 'password'"
                type="password"
                :id="slugify(label)"
                :class="getClass"
                class="transition-colors duration-200"
                :placeholder="placeholder ?? t('placeholder')"
                required
                :value="modelProxy"
                @input="onInput"
                :readonly="readonly"
            />
            <input
                v-else-if="type === 'date'"
                type="date"
                :id="slugify(label)"
                :class="getClass"
                class="transition-colors duration-200 w-full"
                :placeholder="placeholder ?? t('placeholder')"
                required
                :value="modelProxy"
                @input="onInput"
                :readonly="readonly"
            />
            <input
                v-else
                type="text"
                :id="slugify(label)"
                :class="getClass"
                class="transition-colors duration-200"
                :placeholder="placeholder ?? t('placeholder')"
                required
                :value="modelProxy"
                @input="onInput"
                :readonly="readonly"
                :maxlength="requirements?.max_length ? requirements.max_length : width === 'single' ? '2' : ''"
            />
            <LockIcon
                v-if="readonly && width !== 'single'"
                class="pointer-events-none absolute right-3 top-3.5 size-3 text-text-light/70"
                aria-hidden="true"
            />
        </div>
        <p
            v-show="note"
            id="helper-text-explanation"
            class="mt-2 px-2 text-[0.75rem] text-primary-dark"
            v-html="note"
        ></p>
        <p v-show="error" class="text-rose-600 px-1 pt-2">{{ error }}</p>
    </div>
</template>

<script lang="ts" setup>
import { kebabCase } from 'lodash-es'

const { t } = useI18n({ useScope: 'local' })

const emit = defineEmits(['update:modelValue'])

const modelProxy = computed<string | number | LocalizedValue | null>({
    get: () => {
        if (props.properties?.translate && props.locale && model.value && typeof model.value === 'object') {
            return (model.value as LocalizedValue)[props.locale] ?? null
        }
        return model.value as string | number | null
    },
    set: (value: string | number | LocalizedValue | null) => {
        if (props.properties?.translate && props.locale) {
            const current = (model.value as LocalizedValue) ?? {}
            model.value = { ...current, [props.locale]: value as string }
        } else {
            model.value = value
        }
    }
})

type LocalizedValue = Record<string, string>

const model = defineModel<LocalizedValue | string | number | null>('modelValue')

const props = defineProps({
    type: {
        type: String as PropType<'text' | 'textArea' | 'float' | 'int' | 'password' | 'date'>,
        default: 'text'
    },
    label: {
        type: String,
        required: true
    },
    showLabel: Boolean,
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
    },
    default: {
        type: String
    },
    note: {
        type: String
    },
    readonly: {
        type: Boolean
    },
    code: {
        type: Boolean
    },
    width: {
        type: String
    },
    fitContent: {
        type: Boolean,
        default: false
    },
    error: {
        type: String
    },
    translate: {
        type: Boolean,
    },
    renderer: {
        type: String
    },
    properties: Object,
    locale: String,
    bigLabel: Boolean,
    actions: Array<{ name: String, action: String }>,
    maskInput: {
        type: Boolean,
        required: false,
        default: false
    },
    requirements: {
        type: Object as PropType<{
            special?: boolean,
            min_length?: number,
            max_length?: number,
            uppercase?: boolean,
            lowercase?: boolean,
            number?: boolean
        }>,
        required: false
    }
})


const slugify = (value: string) => {
    return kebabCase(value)
}

const validate_value_by_requirements = (test: string) => {
    let validation = {
        is_valid: true,
        checks: {
            special: {
                label: "Must contain special Character",
                is_valid: true
            },
            uppercase: {
                label: "Must contain uppercase character",
                is_valid: true
            },
            lowercase: {
                label: "Must contain lowercase character",
                is_valid: true
            },
            number: {
                label: "Must contain number",
                is_valid: true
            },
            max_length: {
                label: "Contains too many character",
                is_valid: true
            },
            min_length: {
                label: "Not enough characters",
                is_valid: true
            },
        }
    }
    if (!props.requirements) return validation

    validation.checks.special.is_valid = props.requirements.special ? /[!@#$%^&*(),.?":{}|<>]/.test(test) : true
    validation.checks.uppercase.is_valid = props.requirements.uppercase ? /[A-Z]/.test(test) : true
    validation.checks.lowercase.is_valid = props.requirements.lowercase ? /[a-z]/.test(test) : true
    validation.checks.number.is_valid = props.requirements.number ? /[0-9]/.test(test) : true
    validation.checks.min_length.is_valid = typeof props.requirements.min_length === 'number' ? test.length >= props.requirements.min_length : true
    validation.checks.max_length.is_valid = typeof props.requirements.max_length === 'number' ? test.length <= props.requirements.max_length : true

    validation.is_valid = Object.values(validation.checks).every(check => check.is_valid)
    return validation
}

const failing_requirements = ref(validate_value_by_requirements(""))

const onInput = (e: Event) => {

    const input = e.target as HTMLInputElement
    if (props.type === 'textArea' && e.target) {
        if (props.translate && props.locale) {
            modelProxy.value = input.value
        } else emit('update:modelValue', input.value)
    } else {
        if (props.properties?.translate && props.locale) {
            modelProxy.value = input.value
        } else {
            modelProxy.value = input.value
            emit('update:modelValue', input.value)
        }
    }
    if (props.requirements) {
        failing_requirements.value = validate_value_by_requirements(input.value)
    }
}

onMounted(() => {
    if (props.translate && !model.value) {
        emit('update:modelValue', model.value)
    }
})

const getClass = computed(() => {
    let classes = `border border-width-[3px] placeholder:text-text-light text-sm rounded-lg block p-2.5 focus:outline-none`
    // Read-only fields drop the primary border and focus colours so they read as settled values, not inputs
    if (props.readonly) classes += ' border-transparent bg-background-accent-dark/60 text-text/75 cursor-default focus:ring-0 focus:border-transparent'
    else classes += ' border-primary bg-background text-text focus:ring-secondary focus:border-secondary'
    if (props.readonly && props.width !== 'single') classes += ' pr-8'
    if (props.error || !failing_requirements.value.is_valid) classes += ' !focus:border-error !border-error'
    if (props.fitContent) classes += ' input-field--fit-content'
    else if (props.width === 'single') classes += ' w-12 text-center'
    else classes += ' w-full'
    return classes
})

</script>

<style scoped>
.input-field--fit-content {
    width: fit-content;
    max-width: 100%;
    field-sizing: content;
}
</style>

<i18n lang="yaml">
en:
    placeholder: Enter value here
nl:
    placeholder: Vul hier in
</i18n>
